import React, { useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';

// Google Customer Reviews — Survey Opt-in integration.
//
// Rendered on the order confirmation step for orders that include physical
// (shippable) products. It loads Google's platform.js, then calls
// gapi.surveyoptin.render() with the live order details so Google can email
// the customer a review survey around the estimated delivery date.
//
// merchant_id is Motion Reality's Google Merchant Center ID. The estimated
// delivery date is a short buffer over the next-day UK mainland service.

const MERCHANT_ID = '5685452191';
const DELIVERY_BUFFER_DAYS = 3;

export default function GoogleReviewsOptIn({ orderId, email, productIds }) {
  const rendered = useRef(false);

  useEffect(() => {
    if (rendered.current) return;
    if (!orderId || !email) return;
    rendered.current = true;

    (async () => {
      // GTINs are optional — resolve them from the purchased products to
      // improve review matching, but never block the opt-in on a failure.
      const ids = [...new Set((productIds || []).filter(Boolean))];
      const gtins = [];
      for (const id of ids) {
        try {
          const p = await base44.entities.Product.get(id);
          if (p?.gtin) gtins.push(p.gtin);
        } catch (_) {}
      }

      const est = new Date();
      est.setDate(est.getDate() + DELIVERY_BUFFER_DAYS);
      const estimatedDeliveryDate = est.toISOString().slice(0, 10);

      const data = {
        merchant_id: MERCHANT_ID,
        order_id: String(orderId),
        email,
        delivery_country: 'GB',
        estimated_delivery_date: estimatedDeliveryDate,
      };
      if (gtins.length) data.products = gtins.map((g) => ({ gtin: g }));

      window.renderOptIn = function () {
        try {
          window.gapi.load('surveyoptin', function () {
            window.gapi.surveyoptin.render(data);
          });
        } catch (_) {}
      };

      if (window.gapi) {
        window.renderOptIn();
      } else if (!document.getElementById('gcr-platform')) {
        const s = document.createElement('script');
        s.id = 'gcr-platform';
        s.src = 'https://apis.google.com/js/platform.js?onload=renderOptIn';
        s.async = true;
        s.defer = true;
        document.body.appendChild(s);
      }
    })();
  }, [orderId, email, productIds]);

  // Google injects the opt-in dialog itself — no container element needed.
  return null;
}